import { Injectable } from '@angular/core';
import { PollOrderSuccess } from '@features/polling/store/polling.actions';
import { UserPreferenceActionTypes } from '@features/user-preference/store/actions/user-preference.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Order, OrderIndexParams } from '@rd/types/order.type';
import { EMPTY, of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { OrderApiService } from '../../services/order-api/order-api.service';
import { getOrderFeatureState } from '../../store/reducers';
import * as actions from '../actions/order.actions';
import { OrderState } from '../reducers/order.reducer';

@Injectable()
export class OrderEffects {
  constructor(
    private store: Store,
    private actions$: Actions,
    private orderApiService: OrderApiService,
  ) {}

  getOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.OrderActionTypes.GET_ORDERS),
      withLatestFrom(this.store.select(getOrderFeatureState)),
      switchMap(
        ([
          action,
          {
            ordersList: {
              filters: { filter, sort },
            },
          },
        ]: [actions.GetOrders, OrderState]) => {
          return this.orderApiService
            .getOrders({
              filter: filter as OrderIndexParams['filter'],
              sort,
            })
            .pipe(
              map((orders: Order[]) => new actions.GetOrdersSuccess(orders)),
            );
        },
      ),
      catchError(() => of(new actions.GetOrdersFail())),
    ),
  );

  getOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.OrderActionTypes.GET_ORDER),
      withLatestFrom(this.store.select(getOrderFeatureState)),
      switchMap(
        ([
          action,
          {
            ordersMap: { orders },
          },
        ]: [actions.GetOrder, OrderState]) => {
          if (orders[action.payload]) {
            return EMPTY;
          }

          return this.orderApiService
            .getOrder(action.payload)
            .pipe(map((order: Order) => new actions.GetOrderSuccess(order)));
        },
      ),
      catchError(() => of(new actions.GetOrderFail())),
    ),
  );

  updatePointsAccountIdSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPreferenceActionTypes.UPDATE_POINTS_ACCOUNT_ID_SUCCESS),
      map(() => new actions.GetOrders()),
    ),
  );

  updateOrderOnPollingSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PollOrderSuccess),
      map((action) => new actions.GetOrderSuccess(action.order)),
    ),
  );
}
